import { graphql } from 'react-relay';
import type { FC, ReactNode } from 'react';
import React, { useCallback, useMemo, useContext, useEffect, useState, useRef } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	SearchBreadcrumbsProviderSearchBreadcrumbsQuery as SearchBreadcrumbsProviderSearchBreadcrumbsQuery$data,
	SearchBreadcrumbsProviderSearchBreadcrumbsQueryVariables as SearchBreadcrumbsProviderSearchBreadcrumbsQuery$variables,
	SearchBreadcrumbsProviderSearchBreadcrumbsQuery_content_PaginatedContentListWithChild_nodes_Content_ancestors_Content_array,
} from './__types__/SearchBreadcrumbsProviderSearchBreadcrumbsQuery.ts';
export type breadcrumbAncestor = {
	id: string;
	title: string;
	href: string;
};

interface SearchBreadcrumbsContextType {
	getAncestors: (id: string, key: string) => breadcrumbAncestor[];
	getEmoji: (id: string) => string;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
}

export const SearchBreadcrumbsContext = React.createContext<SearchBreadcrumbsContextType>({
	getAncestors: () => [],
	getEmoji: () => '',
	loading: false,
	error: undefined,
});

const resultsTransformer = (results: any[]) => {
	const contentIds: Set<string> = new Set();
	const spaceKeys: Set<string> = new Set();
	results.forEach((result) => {
		if (result?.content?.id && result.content.type !== 'space') {
			contentIds.add(result.content.id);
		}
		if (result?.content?.space?.key) {
			spaceKeys.add(result.content.space.key);
		}
	});
	return [[...contentIds], [...spaceKeys]];
};

const ancestorsTransformer = (
	ancestors: SearchBreadcrumbsProviderSearchBreadcrumbsQuery_content_PaginatedContentListWithChild_nodes_Content_ancestors_Content_array,
): breadcrumbAncestor[] => {
	return Array.from(ancestors).map((ancestor) => {
		return {
			id: ancestor?.id || '',
			title: ancestor?.title || '',
			href: ancestor?.links?.webui ? `${CONTEXT_PATH}${ancestor.links.webui}` : '',
		};
	});
};

type SearchBreadcrumbsProviderProps = {
	results: any[];
	searchLoading: boolean;
	children?: ReactNode;
};

export const SearchBreadcrumbsProvider: FC<SearchBreadcrumbsProviderProps> = ({
	results,
	searchLoading,
	children,
}) => {
	const { toUrl } = useContext(RoutesContext) as any;
	const [loading, setLoading] = useState(searchLoading);
	const contentCountRef = useRef(0);
	const spaceCountRef = useRef(0);
	const [contentMap, setContentMap] = useState<Map<string, breadcrumbAncestor[]>>(new Map());
	const [spaceMap, setSpaceMap] = useState<Map<string, breadcrumbAncestor>>(new Map());
	const [emojiMap, setEmojiMap] = useState<Map<string, string>>(new Map());
	const [contentIds, spaceKeys] = resultsTransformer(results);

	const contentIdsToFetchBreadcrumbs = contentIds.filter((id) => !contentMap.has(id));
	const spaceKeysToFetchBreadcrumbs = spaceKeys.filter((key) => !spaceMap.has(key));

	const skipBreadcrumbsQuery =
		!results.length ||
		!contentIds.length ||
		!contentIdsToFetchBreadcrumbs.length ||
		!spaceKeysToFetchBreadcrumbs.length;

	useEffect(() => {
		if (contentMap.size !== contentCountRef.current || spaceMap.size !== spaceCountRef.current) {
			setLoading(false);
			contentCountRef.current = contentMap.size;
			spaceCountRef.current = spaceMap.size;
		}
	}, [contentMap, spaceMap, contentCountRef, spaceCountRef]);

	const onQueryComplete = useCallback(
		(data: SearchBreadcrumbsProviderSearchBreadcrumbsQuery$data) => {
			const contentData = data?.content?.nodes;
			const spaceData = data?.spaces?.nodes;
			const newContentMap = new Map<string, breadcrumbAncestor[]>();
			const newEmojiMap = new Map<string, string>();
			const newSpaceMap = new Map<string, breadcrumbAncestor>();

			contentData?.forEach((item) => {
				if (item?.id && item.ancestors) {
					const ancestors = ancestorsTransformer(item.ancestors);
					newContentMap.set(item.id, ancestors);
				}
				if (item?.id) {
					const emoji = (item?.emoji?.nodes?.[0]?.value || '')
						.replace(/['"]+/g, '')
						.replace(/null/, '');
					if (emoji) {
						newEmojiMap.set(item.id, emoji);
					}
					if (item.ancestors) {
						const ancestors = ancestorsTransformer(item.ancestors);
						newContentMap.set(item.id, ancestors);
					}
				}
			});
			setEmojiMap(new Map([...emojiMap, ...newEmojiMap]));
			setContentMap(new Map([...contentMap, ...newContentMap]));

			spaceData?.forEach((item) => {
				if (item?.key) {
					const spaceOverviewLink = toUrl(SPACE_OVERVIEW.name, {
						spaceKey: item.alias || item.key,
						query: {
							[SPACE_OVERVIEW.HOMEPAGE_ID_QUERY_KEY]: item?.homepage?.id || undefined,
						},
					});
					const spaceAncestor = {
						id: item.id || item.key,
						href: spaceOverviewLink,
						title: item.name || item.key,
					};
					newSpaceMap.set(item.key, spaceAncestor);
				}
			});
			setSpaceMap(new Map([...spaceMap, ...newSpaceMap]));
		},
		[emojiMap, contentMap, spaceMap, toUrl],
	);
	const { error } = useQueryInterop<
		SearchBreadcrumbsProviderSearchBreadcrumbsQuery$data,
		SearchBreadcrumbsProviderSearchBreadcrumbsQuery$variables
	>(
		graphql`
			query SearchBreadcrumbsProviderSearchBreadcrumbsQuery(
				$spaceKeys: [String]
				$contentIds: [ID]
			) {
				spaces(spaceKeys: $spaceKeys) {
					nodes {
						alias
						id
						key
						name
						homepage {
							id
						}
					}
				}
				content(ids: $contentIds) {
					nodes {
						id
						status
						emoji: properties(key: "emoji-title-published") {
							nodes {
								id
								value
							}
						}
						ancestors {
							id
							title
							links {
								webui
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				contentIds: contentIdsToFetchBreadcrumbs,
				spaceKeys: spaceKeysToFetchBreadcrumbs,
			},
			skip: skipBreadcrumbsQuery,
			errorPolicy: 'all',
			onCompleted: onQueryComplete,
		},
	);

	const getAncestors = useCallback(
		(id: string, key: string) => {
			const ancestors = contentMap.get(id);
			const spaceAncestor = spaceMap.get(key);
			if (spaceAncestor) {
				if (ancestors) {
					if (ancestors.length === 0) {
						ancestors.push(spaceAncestor);
					} else {
						ancestors[0] = spaceAncestor;
					}
					return ancestors;
				} else {
					return [spaceAncestor];
				}
			}
			return [];
		},
		[contentMap, spaceMap],
	);

	const getEmoji = useCallback(
		(id: string) => {
			return emojiMap.get(id) || '';
		},
		[emojiMap],
	);

	const breadcrumbsLoading = results.length > 0 && !error ? loading : false;

	const searchBreadcrumbContextValue = useMemo(
		() => ({
			getAncestors,
			getEmoji,
			loading: breadcrumbsLoading,
			error,
		}),
		[getAncestors, getEmoji, breadcrumbsLoading, error],
	);

	return (
		<SearchBreadcrumbsContext.Provider value={searchBreadcrumbContextValue}>
			{children}
			{error && <ErrorDisplay error={error} />}
		</SearchBreadcrumbsContext.Provider>
	);
};

export const useSearchBreadcrumbs = () => {
	return useContext(SearchBreadcrumbsContext);
};
